import React, { useState } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Message {
  id: number;
  direction: 'outgoing' | 'incoming';
  sender_email?: string;
  recipient_email?: string;
  subject?: string;
  body?: string;
  sent_at?: string;
}

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message | null;
  onReplySent: () => void;
}

export const MessageModal = ({
  isOpen,
  onClose,
  message,
  onReplySent
}: MessageModalProps) => {
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  if (!message) return null;

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error('Napisz treść odpowiedzi');
      return;
    }

    try {
      setSending(true);

      await api.post('/transport/messages/reply', {
        messageId: message.id,
        replyText: replyText.trim()
      });

      toast.success('Odpowiedź wysłana');
      setReplyMode(false);
      setReplyText('');
      onReplySent();
      onClose();
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      toast.error(error?.response?.data?.error || 'Nie udało się wysłać odpowiedzi');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Brak daty';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={message.direction === 'incoming' ? 'Wiadomość przychodząca' : 'Wiadomość wysłana'}
      size="xl"
    >
      <div className="space-y-6">

        {/* Message details */}
        <div className="bg-gray-50 border rounded-lg p-4 space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="text-sm text-gray-600">Od:</div>
              <div className="font-medium">{message.sender_email || 'Nieznany'}</div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              message.direction === 'incoming'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {message.direction === 'incoming' ? 'Otrzymana' : 'Wysłana'}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-600">Do:</div>
            <div className="font-medium">{message.recipient_email || 'Nieznany'}</div>
          </div>

          <div>
            <div className="text-sm text-gray-600">Temat:</div>
            <div className="font-medium">{message.subject || 'Brak tematu'}</div>
          </div>

          <div>
            <div className="text-sm text-gray-600">Data:</div>
            <div className="font-medium">{formatDate(message.sent_at)}</div>
          </div>
        </div>

        {/* Message body */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Treść wiadomości:</h3>
          <div className="border rounded-lg p-4 bg-white max-h-96 overflow-y-auto">
            <div dangerouslySetInnerHTML={{ __html: message.body || '' }} />
          </div>
        </div>

        {/* Reply section */}
        {message.direction === 'incoming' && (
          <div>
            {replyMode ? (
              <>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Twoja odpowiedź:</h3>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="w-full h-40 border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Napisz odpowiedź..."
                />
              </>
            ) : (
              <Button
                onClick={() => setReplyMode(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Odpowiedz
              </Button>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          {replyMode && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setReplyMode(false);
                  setReplyText('');
                }}
                disabled={sending}
              >
                Anuluj odpowiedź
              </Button>
              <Button
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {sending ? 'Wysyłanie...' : 'Wyślij odpowiedź'}
              </Button>
            </>
          )}
          {!replyMode && (
            <Button variant="outline" onClick={onClose}>
              Zamknij
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
